// ── Adapter do IWorkspaceWebhookGateway ───────────────────────────
// O disparo passa pela Edge Function `workspace-webhook`, que é quem guarda
// o segredo, monta o payload a partir do banco, assina (HMAC-SHA256) e
// entrega. Daqui só saem IDs — o browser nunca vê a URL nem o segredo do
// destino, e não consegue forjar o conteúdo enviado ao cliente.

import { getSupabase } from '@infra/supabase/client';
import { edgeFunctions } from '@config/index';
import type {
  IWorkspaceWebhookGateway,
  DispatchWebhookResult,
  DispatchWebhookParams,
} from '@modules/webhooks';

interface EdgeResponse {
  ok?: boolean;
  status?: string;
  delivery_id?: string | null;
  error?: string | null;
}

export class SupabaseWorkspaceWebhookGateway implements IWorkspaceWebhookGateway {
  dispatch = async ({
    event,
    workspaceId,
    leadId,
    dedupeKey = null,
    data = null,
  }: DispatchWebhookParams): Promise<DispatchWebhookResult> => {
    const supabase = getSupabase();
    if (!supabase) return { status: 'failed', error: 'Supabase não inicializado.' };

    const { data: response, error } = await supabase.functions.invoke<EdgeResponse>(
      edgeFunctions.workspaceWebhook,
      {
        body: {
          event,
          workspace_id: workspaceId,
          lead_id: leadId,
          dedupe_key: dedupeKey,
          data: data || {},
        },
      },
    );

    // A função responde 502 quando o DESTINO falhou; o supabase-js transforma
    // isso em `error`. A entrega já ficou registrada em webhook_deliveries, então
    // aqui só reportamos — não há retry no cliente.
    if (error) return { status: 'failed', error: error.message || 'Falha ao invocar o webhook.' };

    return {
      status: response?.status || (response?.ok ? 'delivered' : 'failed'),
      deliveryId: response?.delivery_id ?? null,
      error: response?.error ?? null,
    };
  };
}
